import SafeHtml from "@lhd/components/SafeHtml";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import Attachments from "../../../components/Attachments";
import { AppRecordValueV2, CONTRACT_STATUS_LABEL } from "@lhd/types";
import { AppSelectionList } from "@lhd/types";
import { isArray } from "lodash";
import TextToolTip from "@lhd/components/TextToolTip";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import {
  SELECTIONS_APP_PARTS,
  SEPARATE,
} from "@lhd/components/CloneApp/constants";
import { getPrefixIcon } from "@lhd/features/Apps/utils";
import { Fragment } from "react";

interface IProps {
  appRecordValue?: AppRecordValueV2;
  appPart: AppPartType;
  selectionDataList?: AppSelectionList;
  record?: any;
}

const CellValue = ({
  appRecordValue,
  appPart,
  selectionDataList,
  record,
}: IProps) => {
  const renderIconPrefixSelections = (
    appPart: AppPartType,
    valueFormat?: string,
  ) => {
    if (!valueFormat) return null;
    const valueArray =
      (valueFormat as string)?.split(SEPARATE)?.filter((x) => !!x) || [];
    return (
      <TextToolTip>
        {valueArray?.map((labelRaw, index) => {
          const label =
            index !== valueArray.length - 1 ? labelRaw + SEPARATE : labelRaw;
          return (
            <Fragment key={index}>
              {getPrefixIcon(appPart.app_part_type)}
              {label}
            </Fragment>
          );
        })}
      </TextToolTip>
    );
  };

  if (appPart.app_part_type === APP_PART_TYPES.RICH_TEXT) {
    const html = (appRecordValue?.value || "") as string;
    return (
      <TextToolTip
        className="wrap-cell-rich_text"
        textTooltip={<SafeHtml html={html} />}
      >
        <SafeHtml html={html} />
      </TextToolTip>
    );
  }
  if (appPart.app_part_type === APP_PART_TYPES.ATTACHMENT) {
    return (
      <Attachments
        attachments={
          isArray(appRecordValue?.valueFormat)
            ? appRecordValue?.valueFormat
            : []
        }
      />
    );
  }

  // Handle WORKER_SELECTION and REPRESENTATIVE_SELECTION: convert IDs to names
  if (
    appPart.app_part_type === APP_PART_TYPES.WORKER_SELECTION ||
    appPart.app_part_type === APP_PART_TYPES.REPRESENTATIVE_SELECTION
  ) {
    if (appRecordValue?.valueFormat) {
      return renderIconPrefixSelections(
        appPart,
        appRecordValue.valueFormat as string,
      );
    }
    const value = appRecordValue?.value;
    const selectionList =
      appPart.app_part_type === APP_PART_TYPES.REPRESENTATIVE_SELECTION
        ? selectionDataList?.representative_selection
        : selectionDataList?.worker_selection;

    if (!value || !selectionList?.length) {
      return <div></div>;
    }

    // Ensure value is array
    const workerIds = Array.isArray(value)
      ? value.map((id) => String(id))
      : [String(value)];

    const workerNames = workerIds
      .map((id) => {
        const worker = selectionList?.find(
          (w: any) => String(w.id) === String(id),
        );
        return (
          (worker?.first_name && worker?.last_name
            ? `${worker.first_name} ${worker.last_name}`
            : worker?.full_name || worker?.name) || id
        );
      })
      .join(SEPARATE + " ");
    return <TextToolTip>{workerNames}</TextToolTip>;
  }

  if (SELECTIONS_APP_PARTS.includes(appPart.app_part_type)) {
    return renderIconPrefixSelections(
      appPart,
      appRecordValue?.valueFormat as string,
    );
  }

  // Handle STATUS_CONTRACT: display in Japanese
  if (appPart.app_part_type === APP_PART_TYPES.STATUS_CONTRACT) {
    if (appRecordValue?.valueFormat) {
      return <TextToolTip>{String(appRecordValue.valueFormat)}</TextToolTip>;
    }
    const status = record?.contract_info?.contract_status;
    const displayText = status ? CONTRACT_STATUS_LABEL[status] || status : "";
    return <TextToolTip>{displayText}</TextToolTip>;
  }

  // Handle settlement_date: format to YYYY年M月度
  if (appPart.field_code === "settlement_date") {
    const val = appRecordValue?.value;
    if (!val) return <TextToolTip>{""}</TextToolTip>;
    const date = dayjs(val);
    const displayText = date.isValid()
      ? date.format(DATE_TIME_FORMAT.MONTH_YEAR_JP + "度")
      : String(val);
    return <TextToolTip>{displayText}</TextToolTip>;
  }

  if (
    typeof appRecordValue?.valueFormat === "string" &&
    appRecordValue?.valueFormat?.split("\n").length > 1
  ) {
    return (
      <TextToolTip isTextMultipleLine>
        {appRecordValue?.valueFormat}
      </TextToolTip>
    );
  }
  return <TextToolTip>{appRecordValue?.valueFormat}</TextToolTip>;
};

export default CellValue;
